import {
  weatherCodeEmoji,
  weatherCodeLabel,
} from "@/lib/conditions/weather-code-display";
import { scoreBadgeColor } from "@/lib/conditions/scoring";
import { dayLabel, dateLabel } from "@/lib/conditions/forecast-day-format";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { ScoreConfidenceBadge } from "./ScoreConfidenceBadge";
import type { SkiScoreConfidenceMetadata } from
  "@/lib/conditions/ski-score-confidence";
import {
  forecastDayWithheldLabel,
  hasSkiV3,
  skiClosedReason,
  skiLikelyRange,
  skiScoreTooltip,
  type SkiConfidenceClass,
} from "./ski-board-display";
import { SkiClassChip } from "./SkiScoreChips";

type SkiForecastDay = {
  date: string;
  weatherCode: number | null;
  weatherAvailable?: boolean;
  tempMaxC: number | null;
  tempMinC: number | null;
  snowfallCm: number | null;
  windSpeedKmh: number | null;
  score?: number;
  label?: string;
  scoreAvailable?: boolean;
  snowDepthConfidence?: SkiScoreConfidenceMetadata;
  /** Storm-day score leans on the location's tree skiing (off-piste only). */
  assumesTrees?: boolean;
  /**
   * Ski v3 fields for the active sport (buildMountainForecastDays). Absent on revision 2
   * days, which keep today's badge. A withheld day has label "Closed" or "No lift access".
   */
  status?: string;
  statusReason?: string;
  cls?: SkiConfidenceClass;
  /** P10, 0 to 100 integer. */
  lo?: number;
  /** P90, 0 to 100 integer. */
  hi?: number;
};

/** A withheld day: "Closed" (or "No lift access") instead of a number, with the reason. */
function ClosedPill({ label, day }: { label: string; day: SkiForecastDay }) {
  const reason = skiClosedReason(day);
  const detail = reason && reason !== label ? reason : null;
  return (
    <>
      <div
        className="rounded-[6px] px-1.5 py-0.5 text-center mt-1.5 bg-slate-100 border border-slate-200 text-slate-600"
        title={detail ? `${label}. ${detail}` : label}
        data-ski-withheld={label}
      >
        <span className="text-[12px] font-semibold">{label}</span>
      </div>
      {detail && (
        <div className="mt-1 text-[10px] text-text-muted">{detail}</div>
      )}
    </>
  );
}

function ScorePill({ score, title }: { score: number; title?: string }) {
  const badge = scoreBadgeColor(score);
  return (
    <div
      className={`rounded-[6px] px-1.5 py-0.5 text-center mt-1.5 ${badge.bg} ${badge.text}`}
      title={title}
    >
      <span className="font-serif text-[13px] font-bold tabular-nums">
        {scoreOutOf100(score)}
      </span>
    </div>
  );
}

export function SkiForecastStrip({
  forecast,
  todayISO,
  highlightDate,
  busyPeriodLabel,
}: {
  forecast: SkiForecastDay[];
  todayISO: string;
  highlightDate?: string;
  busyPeriodLabel?: string | null;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 md:gap-3">
      {forecast.map((d) => {
        const withheld = forecastDayWithheldLabel(d);
        const v3 = hasSkiV3(d);
        const likely = v3 ? skiLikelyRange(d.lo, d.hi) : null;
        // A withheld day is never the best day, whatever the caller passes.
        const isBest = d.date === highlightDate && !withheld;
        return (
          <div
            key={d.date}
            className={`rounded-[12px] border p-3.5 text-center transition-colors ${
              isBest
                ? "bg-gold/5 border-gold/40 ring-1 ring-gold/30"
                : "bg-white border-border-light"
            }`}
          >
            <div className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted">
              {dayLabel(d.date, todayISO)}
            </div>
            <div className="text-[10px] text-text-muted/80 mb-2">
              {dateLabel(d.date)}
            </div>
            <div
              className="text-[28px] leading-none my-2"
              aria-label={weatherCodeLabel(d.weatherCode ?? -1)}
              title={weatherCodeLabel(d.weatherCode ?? -1)}
            >
              {weatherCodeEmoji(d.weatherCode ?? -1)}
            </div>
            {withheld
              ? <ClosedPill label={withheld} day={d} />
              : d.score !== undefined && d.scoreAvailable !== false && v3
              ? (
                <>
                  <ScorePill score={d.score} title={skiScoreTooltip(d.label ?? null, d) ?? undefined} />
                  {likely && (
                    <div className="mt-1 text-[10px] font-medium text-text-muted tabular-nums">
                      {likely}
                    </div>
                  )}
                  <div className="mt-1 flex justify-center empty:hidden">
                    <SkiClassChip fields={d} label={d.label ?? null} />
                  </div>
                </>
              )
              : d.score !== undefined && d.scoreAvailable !== false
              ? (
                <>
                  <ScorePill score={d.score} />
                  {d.snowDepthConfidence && (
                    <div className="mt-1 flex justify-center">
                      <ScoreConfidenceBadge {...d.snowDepthConfidence} />
                    </div>
                  )}
                </>
              )
              : d.scoreAvailable === false
                ? (
                  <div className="mt-1.5 flex justify-center">
                    <ScoreConfidenceBadge
                      {...(d.snowDepthConfidence ?? {
                        confidence: "unavailable",
                        reason: "missing-provenance",
                        lo: null,
                        hi: null,
                        depthGate: null,
                        suppressionApplied: null,
                      })}
                    />
                  </div>
                )
                : null}
            <div className="flex items-baseline justify-center gap-1.5 mb-1 mt-1.5">
              <span className="font-serif text-[18px] font-bold text-navy tabular-nums">
                {d.tempMaxC == null ? "Unavailable" : `${d.tempMaxC.toFixed(0)}°`}
              </span>
              <span className="text-[12px] text-text-muted tabular-nums">
                {d.tempMinC == null ? "" : `${d.tempMinC.toFixed(0)}°`}
              </span>
            </div>
            {d.snowfallCm != null && d.snowfallCm > 0 ? (
              <div className="text-[12px] font-semibold text-navy inline-flex items-center gap-1">
                <span aria-hidden>❄️</span>
                {d.snowfallCm.toFixed(1)} cm
              </div>
            ) : (
              <div className="text-[11px] text-text-muted/70">{d.snowfallCm == null ? "Snowfall unavailable" : "No new snow"}</div>
            )}
            {d.assumesTrees && (
              <div
                className="text-[10px] font-semibold text-emerald-700 mt-0.5 inline-flex items-center gap-0.5"
                title="Storm-day score assumes skiing below the treeline"
                aria-label="Storm-day score assumes skiing below the treeline"
              >
                <span aria-hidden>🌲</span> In the trees
              </div>
            )}
            <div className="text-[11px] text-text-muted mt-1">
              {d.windSpeedKmh == null ? "Wind unavailable" : `${d.windSpeedKmh.toFixed(0)} km/h wind`}
            </div>
            {isBest && (
              <div className="text-[10px] font-semibold text-gold mt-1.5 uppercase tracking-[1px]">
                Best day
              </div>
            )}
          </div>
        );
      })}
      {busyPeriodLabel && (
        <div className="col-span-full mt-1">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            {busyPeriodLabel}: expect busier slopes this week
          </span>
        </div>
      )}
    </div>
  );
}
